import React, { useState } from 'react';

interface Props {
  unresolvedCount: number;
  hasHumanRequired: boolean;
  autoCorrects: { fromSpeakerId: string; toSpeakerId: string; segmentCount: number; convergenceScore: number }[];
  onSignAnyway: () => void;
  onReview: () => void;
  onSignWithReason: (reason: string) => void;
  onClose: () => void;
}

export function SignBlockModal({
  unresolvedCount, hasHumanRequired, autoCorrects, onSignAnyway, onReview, onSignWithReason, onClose,
}: Props) {
  const [reason, setReason] = useState('');

  const reasonValid = reason.trim().length > 0;

  return (
    <div role="dialog" aria-label="Speaker alerts unresolved" className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-xl w-[480px] p-6 relative">
        <h2 className="text-lg font-semibold mb-2">Speaker alerts unresolved</h2>
        <p className="text-sm text-gray-700 mb-4">
          {unresolvedCount} speaker {unresolvedCount === 1 ? 'alert' : 'alerts'} on this encounter
          {unresolvedCount === 1 ? ' has' : ' have'} not been addressed.
        </p>
        {autoCorrects.length > 0 && (
          <section className="mb-4 p-3 bg-slate-50 border-l-4 border-slate-500 text-sm">
            <p className="font-medium mb-1">&#9881; System changes you may want to review:</p>
            <ul className="list-disc list-inside space-y-1">
              {autoCorrects.map((a, i) => (
                <li key={i}>
                  {a.segmentCount} segment(s) reassigned: <code>{a.fromSpeakerId}</code> → <code>{a.toSpeakerId}</code>
                  {' '}(auto-correct, {(a.convergenceScore * 100).toFixed(0)}% confidence)
                </li>
              ))}
            </ul>
          </section>
        )}
        <textarea
          aria-label="Reason for signing without review"
          placeholder="(Optional) If signing without review, briefly explain why."
          className="w-full border rounded p-2 text-sm h-24 mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onReview}
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
          >
            Review
          </button>
          <button
            type="button"
            onClick={onSignAnyway}
            disabled={hasHumanRequired}
            className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
            title={hasHumanRequired ? 'Resolve human-required flags before signing' : ''}
          >
            Sign anyway
          </button>
          <button
            type="button"
            disabled={!reasonValid}
            onClick={() => onSignWithReason(reason.trim())}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Sign + reason
          </button>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500"
        >
          ×
        </button>
      </div>
    </div>
  );
}
